import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Select,
  Checkbox,
  FormControlLabel,
  Chip,
} from '@mui/material'
import styles from './Editor.module.css'
import React, { ChangeEvent, useEffect, useState } from 'react'
import {
  emptyTreatmentDTO,
  fromModel,
  TreatmentDTO,
} from '../../modules/treatments/models/TreatmentDTO'
import { getContentContainer } from '../../container/treatment-module'
import { ITreatmentService, TREATMENT_SERVICE_KEY } from '../../modules/treatments'
import { Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import genericStyle from '../../common/utils/generic.module.css'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { navigate } from '@reach/router'
import { ROUTE_DASHBOARD } from '../../routes/routes-constants'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { getUserContainer } from '../../container/user-module'
import { useIsOpenMenuLeftContext } from '../../common/utils/isOpenMenuLeftContext'
import { Input } from '../../pages/user-profile/userProfileStylesMui'
import { InputTextDescription } from '../resource/userProfileStylesMui'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import calendarIcon from '../../assets/calendar/calendar-newEvent.svg'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import style from '../calendar/Editor.module.css'
import dayjs, { Dayjs } from 'dayjs'

type TreatmentsEditorProps = {
  id?: string
  userID?: string
}

type DurationBadge = {
  id: string
  label: string
  days: number
}

const userContainer = getUserContainer()
const treatmentService = getContentContainer().get<ITreatmentService>(TREATMENT_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

const calculateDuration = (
  startDate?: Date | null,
  endDate?: Date | null,
  currently?: boolean
): number => {
  if (!startDate) return 0

  if (currently) {
    const start = new Date(startDate)
    const end = new Date()

    const startNormalized = new Date(start.getFullYear(), start.getMonth(), start.getDate())
    const endNormalized = new Date(end.getFullYear(), end.getMonth(), end.getDate())

    if (endNormalized < startNormalized) {
      return 0
    }

    const diffTime = endNormalized.getTime() - startNormalized.getTime()
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    return diffDays + 1
  }

  if (!endDate) return 0

  const start = new Date(startDate)
  const end = new Date(endDate)

  const startNormalized = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const endNormalized = new Date(end.getFullYear(), end.getMonth(), end.getDate())

  if (endNormalized < startNormalized) {
    return 0
  }

  const diffTime = endNormalized.getTime() - startNormalized.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  return diffDays + 1
}

const convertToDate = (date: any): Date => {
  if (!date) return new Date()
  if (date instanceof Date) return date
  if (typeof date === 'string') return new Date(date)
  return new Date()
}

const FREQUENCY_OPTIONS = [4, 6, 8, 12, 24]
const DOSEUNIT_OPTIONS = ['mg', 'gr', 'ml']

export function Editor(props: TreatmentsEditorProps) {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [treatment, setTreatment] = useState<TreatmentDTO>(emptyTreatmentDTO(props.userID || ''))
  const [DateTimePickerError, setDateTimePickerError] = useState<string>('')
  const [dateError, setDateError] = useState<string>('')
  const [currently, setCurrently] = useState<boolean>(false)
  const [durationText, setDurationText] = useState<string>('')
  const [selectedBadge, setSelectedBadge] = useState<string | null>(null)
  const [durationBadges, setDurationBadges] = useState<DurationBadge[]>([
    { id: '3days', label: '3 días', days: 2 },
    { id: '1week', label: '1 semana', days: 6 },
    { id: '1month', label: '1 mes', days: 29 },
    { id: '6months', label: '6 meses', days: 182 },
    { id: '1year', label: '1 año', days: 364 },
  ])
  const { openMenuLeft } = useIsOpenMenuLeftContext()

  useEffect(() => {
    if (!props.id) {
      setCurrently(false)
      const initialTreatment = emptyTreatmentDTO(props.userID || '')
      setTreatment({
        ...initialTreatment,
        dose: '0',
        currently: false,
      })
      return
    }

    treatmentService.getByID(props.id).subscribe((res) => {
      if (!res) return
      const treatmentData = fromModel(res)

      const isCurrently = treatmentData.currently === true

      setCurrently(isCurrently)
      setTreatment({
        ...treatmentData,
        startDate: convertToDate(treatmentData.startDate),
        endDate: isCurrently ? undefined : convertToDate(treatmentData.endDate),
        dose: String(treatmentData.dose || '0'),
        doseUnit: treatmentData.doseUnit ? String(treatmentData.doseUnit) : '',
      })
    })
  }, [props.id])

  useEffect(() => {
    if (treatment.startDate) {
      if (currently) {
        setDurationText(t('durationTextCurrently'))
      } else {
        const calculatedDuration = calculateDuration(
          treatment.startDate,
          treatment.endDate,
          currently
        )

        setTreatment((prev) => ({
          ...prev,
          duration: calculatedDuration,
        }))

        if (calculatedDuration === 0) {
          setDurationText(t('durationTextZero'))
        } else if (calculatedDuration === 1) {
          setDurationText(t('durationTextSingle'))
        } else {
          setDurationText(t('durationTextPlural', { days: calculatedDuration }))
        }

        const matchingBadge = durationBadges.find((badge) => {
          // Para badges pequeños (3, 7, 30 días), la duración es days + 1
          // Para 6 meses y 1 año, la duración coincide con days debido a cómo se manejan las fechas
          if (badge.id === '6months') {
            return calculatedDuration === 182
          } else if (badge.id === '1year') {
            return calculatedDuration === 365
          } else {
            return calculatedDuration === badge.days + 1
          }
        })

        if (matchingBadge && treatment.endDate && !currently) {
          setSelectedBadge(matchingBadge.id)
        } else {
          setSelectedBadge(null)
        }
      }
    } else {
      if (currently) {
        setDurationText(t('durationTextCurrentlyNoStartDate'))
      } else {
        setDurationText(t('durationTextNoDate'))
      }
      setSelectedBadge(null)
    }
  }, [treatment.startDate, treatment.endDate, currently, t, durationBadges])

  const handleTreatment = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setTreatment((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target
    setTreatment((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleStartDate = (event: Dayjs | null) => {
    if (event) {
      const newStartDate = event.toDate()
      const startDateObj = new Date(newStartDate)
      const endDateObj = currently
        ? undefined
        : treatment.endDate
        ? convertToDate(treatment.endDate)
        : undefined

      if (!currently && endDateObj) {
        const startNormalized = new Date(
          startDateObj.getFullYear(),
          startDateObj.getMonth(),
          startDateObj.getDate()
        )
        const endNormalized = new Date(
          endDateObj.getFullYear(),
          endDateObj.getMonth(),
          endDateObj.getDate()
        )

        if (startNormalized > endNormalized) {
          setDateError(t('startDateAfterEndDateError'))
        } else {
          setDateError('')
        }
      } else {
        setDateError('')
      }

      const updatedTreatment = Object.assign({ ...treatment }, { startDate: startDateObj })
      if (currently) {
        updatedTreatment.endDate = undefined
      }
      setTreatment(updatedTreatment)

      setSelectedBadge(null)
    }
  }

  const handleEndDate = (event: Dayjs | null) => {
    if (event && !currently) {
      const newEndDate = event.toDate()
      const endDateObj = new Date(newEndDate)
      const startDateObj = treatment.startDate ? convertToDate(treatment.startDate) : new Date()

      if (startDateObj) {
        const startNormalized = new Date(
          startDateObj.getFullYear(),
          startDateObj.getMonth(),
          startDateObj.getDate()
        )
        const endNormalized = new Date(
          endDateObj.getFullYear(),
          endDateObj.getMonth(),
          endDateObj.getDate()
        )

        if (endNormalized < startNormalized) {
          setDateError(t('endDateBeforeStartDateError'))
        } else {
          setDateError('')
        }
      } else {
        setDateError('')
      }

      setTreatment(Object.assign({ ...treatment }, { endDate: endDateObj }))

      setSelectedBadge(null)
    }
  }

  const handleCurrentlyCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked
    setCurrently(isChecked)

    setTreatment((prev) => ({
      ...prev,
      currently: isChecked,
      endDate: isChecked ? undefined : prev.endDate || new Date(),
    }))

    if (isChecked) {
      setDateError('')
      setSelectedBadge(null)
    }
  }

  const handleBadgeClick = (badge: DurationBadge) => {
    if (currently) {
      return
    }

    if (!treatment.startDate) {
      return
    }

    if (selectedBadge === badge.id) {
      setSelectedBadge(null)
      setTreatment((prev) => ({
        ...prev,
        endDate: undefined,
      }))
    } else {
      const startDate = convertToDate(treatment.startDate)
      const endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + badge.days)

      // Normalizar la fecha a medianoche
      const normalizedEndDate = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate()
      )

      setSelectedBadge(badge.id)
      setTreatment((prev) => ({
        ...prev,
        endDate: normalizedEndDate,
      }))
      setDateError('')
    }
  }

  const handleBack = () => navigate(`${ROUTE_DASHBOARD}/${treatment.userID}`)

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()

    const startDate = convertToDate(treatment.startDate)
    let endDate = currently
      ? undefined
      : treatment.endDate
      ? convertToDate(treatment.endDate)
      : undefined

    if (!currently && startDate && endDate && startDate > endDate) {
      setDateError(t('startDateAfterEndDateError'))
      return
    }

    if (loggedUser?.id) {
      const newTreatment: TreatmentDTO = {
        id: treatment.id,
        userID: treatment.userID,
        frequency: treatment.frequency,
        reason: treatment.reason,
        duration: treatment.duration,
        name: treatment.name,
        dose: treatment.dose,
        doseUnit: treatment.doseUnit,
        startDate: convertToDate(treatment.startDate),
        endDate: currently ? undefined : endDate,
        creatorID: loggedUser?.id,
        currently: currently,
      }

      if (props.id) {
        treatmentService.update(newTreatment).subscribe((res) => {
          navigate(`${ROUTE_DASHBOARD}/${treatment.userID}`)
        })
      } else {
        treatmentService.add(newTreatment).subscribe((res) => {
          navigate(`${ROUTE_DASHBOARD}/${treatment.userID}`)
        })
      }
    }
  }

  const handleSubmitButton = () => {
    const form = document.querySelector('form') as HTMLFormElement
    if (form) {
      form.requestSubmit()
    }
  }

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        marginLeft: openMenuLeft ? '50px' : '',
      }}
    >
      <div
        style={{ maxWidth: openMenuLeft ? 'calc(100% - 250px)' : 'calc(100% - 300px)' }}
        className={genericStyle.pageContainer}
      >
        <div className={genericStyle.headerContainer}>
          <Typography>{t('newTreatment')}</Typography>
        </div>
        <div className={genericStyle.container} style={{ minHeight: 'unset', maxHeight: 'unset' }}>
          <>
            <form style={{ width: '100%' }} className={styles.form} onSubmit={handleSave}>
              <Grid style={{ width: '100%' }} item className={styles.box}>
                <Box mb={2}>
                  <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
                    {t('name')}
                  </p>
                  <Input
                    fullWidth
                    value={treatment.name}
                    id="name"
                    name="name"
                    label={''}
                    placeholder={t('namePlaceholder')}
                    variant="outlined"
                    required={true}
                    onChange={(e) => handleTreatment(e)}
                    className={styles.inputField}
                  />
                </Box>

                <Box
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  mb={2}
                  width={'100%'}
                >
                  <Box display={'flex'} alignItems={'center'} mr={4}>
                    <Typography
                      style={{ marginBottom: 0, marginRight: 20 }}
                      className={style.label}
                    >
                      {t('startDate')}
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                      <DatePicker
                        key={'startDate'}
                        inputFormat={'DD/MM/YYYY'}
                        onChange={handleStartDate}
                        value={treatment.startDate ? dayjs(treatment.startDate) : null}
                        label={''}
                        onError={(reason) => {
                          switch (reason) {
                            case 'invalidDate':
                              setDateTimePickerError(t('invalidDateMessage'))
                              break
                            case 'minDate':
                              setDateTimePickerError(t('minDateMessage'))
                              break
                            default:
                              setDateTimePickerError('')
                          }
                        }}
                        renderInput={(props) => (
                          <TextField
                            id="startDate"
                            required
                            sx={{
                              '& fieldset': {
                                borderRadius: 32,
                                border: '1px solid #E8E7EC',
                                fontFamily: 'Montserrat, sans-serif',
                              },
                              '& .MuiInputBase-root': { fontFamily: 'Montserrat, sans-serif' },
                              '& .MuiOutlinedInput-root': {
                                border: 'none',
                                '&.Mui-focused fieldset': {
                                  borderColor: '#ADB84E',
                                },
                              },
                            }}
                            size={'small'}
                            helperText={props.error && DateTimePickerError}
                            {...props}
                          />
                        )}
                        components={{
                          OpenPickerIcon: () => (
                            <img src={calendarIcon} alt="calendar" style={{ marginRight: 8 }} />
                          ),
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                  <Box display={'flex'} alignItems={'center'}>
                    <Box mr={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={currently}
                            onChange={handleCurrentlyCheck}
                            name="currently"
                            className={styles.checkbox}
                          />
                        }
                        label={t('noEndDate')}
                      />
                    </Box>

                    <Box display={'flex'} alignItems={'center'}>
                      <Typography
                        style={{ marginBottom: 0, marginRight: 20 }}
                        className={style.label}
                      >
                        {t('finishDate')}
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                        <DatePicker
                          key={'finishDate'}
                          inputFormat={'DD/MM/YYYY'}
                          onChange={handleEndDate}
                          value={!currently && treatment.endDate ? dayjs(treatment.endDate) : null}
                          label={''}
                          disabled={currently}
                          onError={(reason) => {
                            switch (reason) {
                              case 'invalidDate':
                                setDateTimePickerError(t('invalidDateMessage'))
                                break
                              case 'minDate':
                                setDateTimePickerError(t('minDateMessage'))
                                break
                              default:
                                setDateTimePickerError('')
                            }
                          }}
                          renderInput={(props) => (
                            <TextField
                              id="finishDate"
                              required={!currently}
                              disabled={currently}
                              sx={{
                                '& fieldset': {
                                  borderRadius: 32,
                                  border: '1px solid #E8E7EC',
                                  fontFamily: 'Montserrat, sans-serif',
                                },
                                '& .MuiInputBase-root': { fontFamily: 'Montserrat, sans-serif' },
                                '& .MuiOutlinedInput-root': {
                                  border: 'none',
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#ADB84E',
                                  },
                                },
                                '& .Mui-disabled': {
                                  backgroundColor: '#f5f5f5',
                                  cursor: 'not-allowed',
                                },
                              }}
                              size={'small'}
                              helperText={props.error && DateTimePickerError}
                              {...props}
                            />
                          )}
                          components={{
                            OpenPickerIcon: () => (
                              <img src={calendarIcon} alt="calendar" style={{ marginRight: 8 }} />
                            ),
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                  </Box>
                </Box>

                <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} mb={2}>
                  <Box mr={4} display={'flex'} alignItems={'center'}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666666',
                        fontSize: '14px',
                        minWidth: '120px',
                        fontStyle: 'italic',
                      }}
                    >
                      {durationText}
                    </Typography>
                  </Box>

                  {!currently && (
                    <Box display="flex" alignItems="center" gap={1}>
                      {durationBadges.map((badge) => (
                        <Chip
                          key={badge.id}
                          label={badge.label}
                          clickable
                          onClick={() => handleBadgeClick(badge)}
                          color={selectedBadge === badge.id ? 'primary' : 'default'}
                          variant={selectedBadge === badge.id ? 'filled' : 'outlined'}
                          className={
                            selectedBadge === badge.id ? styles.selectedBadge : styles.badge
                          }
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {dateError && (
                  <Box mb={2}>
                    <Typography color="error" variant="body2">
                      {dateError}
                    </Typography>
                  </Box>
                )}

                <Box display={'flex'} alignItems={'center'} mb={2} gap={4}>
                  <Box flex={1}>
                    <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
                      {t('dose')}
                    </p>
                    <Input
                      fullWidth
                      value={treatment.dose || 0}
                      id="dose"
                      name="dose"
                      label={''}
                      size={'medium'}
                      variant="outlined"
                      type={'number'}
                      required={true}
                      inputProps={{
                        min: 0.01,
                        step: '0.01',
                      }}
                      onChange={(e) => handleTreatment(e)}
                      className={styles.inputField}
                    />
                  </Box>

                  <Box flex={1}>
                    <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
                      {t('doseUnit')}
                    </p>
                    <Select
                      fullWidth
                      id="doseUnit"
                      name="doseUnit"
                      value={treatment.doseUnit || ''}
                      required={true}
                      displayEmpty
                      onChange={handleSelectChange}
                      variant="outlined"
                      className={styles.frequencySelect}
                      MenuProps={{
                        classes: {
                          paper: styles.frequencyMenu,
                        },
                        PaperProps: {
                          className: styles.frequencyMenu,
                        },
                      }}
                    >
                      <MenuItem value="" disabled className={styles.placeholderMenuItem}>
                        {t('selectDoseUnit')}
                      </MenuItem>
                      {DOSEUNIT_OPTIONS.map((doseUnit) => (
                        <MenuItem
                          key={doseUnit}
                          value={doseUnit}
                          className={styles.frequencyMenuItem}
                        >
                          {doseUnit}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  <Box flex={1}>
                    <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
                      {t('frequency')}
                    </p>
                    <Select
                      fullWidth
                      id="frequency"
                      name="frequency"
                      value={treatment.frequency || ''}
                      required={true}
                      displayEmpty
                      onChange={handleSelectChange}
                      variant="outlined"
                      className={styles.frequencySelect}
                      MenuProps={{
                        classes: {
                          paper: styles.frequencyMenu,
                        },
                        PaperProps: {
                          className: styles.frequencyMenu,
                        },
                      }}
                    >
                      <MenuItem value="" disabled className={styles.placeholderMenuItem}>
                        {t('selectFrequency')}
                      </MenuItem>
                      {FREQUENCY_OPTIONS.map((hours) => (
                        <MenuItem key={hours} value={hours} className={styles.frequencyMenuItem}>
                          {hours} {t('hours')}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>

                <Box>
                  <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
                    {t('reason')}
                  </p>
                  <InputTextDescription
                    fullWidth
                    multiline
                    rows={6}
                    value={treatment.reason}
                    id="reason"
                    name="reason"
                    label={''}
                    placeholder={t('reasonPlaceholder')}
                    size={'medium'}
                    variant="outlined"
                    required={true}
                    onChange={handleTreatment}
                    className={styles.textAreaField}
                  />
                </Box>
                <Grid item lg={6}>
                  <Box
                    className={styles.buttons}
                    mb={3}
                    sx={{ width: '100% !important', display: 'flex', justifyContent: 'flex-end' }}
                  >
                    <Box mr={3}>
                      <AppButton
                        theme={ButtonTheme.NewSecondary}
                        type={'button'}
                        label={t('back')}
                        handler={handleBack}
                      />
                    </Box>
                    <AppButton
                      theme={ButtonTheme.NewPrimary}
                      type={'button'}
                      label={t('add')}
                      handler={!!dateError ? () => {} : handleSubmitButton}
                    />
                  </Box>
                </Grid>
              </Grid>
            </form>
          </>
        </div>
      </div>
    </div>
  )
}
